export function normalizeImeiCode(value) {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

export function validateImeiCode(imeiCode) {
  const code = normalizeImeiCode(imeiCode)
  if (!code) {
    return { valid: false, message: 'Mã IMEI không được để trống!' }
  }
  if (code.length > 20) {
    return {
      valid: false,
      message: `Mã IMEI ${code} vượt quá số ký tự cho phép (tối đa 20 ký tự)!`,
    }
  }
  if (/\s/.test(code)) {
    return {
      valid: false,
      message: `Mã IMEI ${code} không được chứa khoảng trắng!`,
    }
  }
  if (!/^[a-zA-Z0-9/_-]+$/.test(code)) {
    return {
      valid: false,
      message: `Mã IMEI ${code} không được chứa ký tự đặc biệt (chỉ cho phép chữ cái, số, /, - và _)!`,
    }
  }
  return { valid: true, message: '' }
}

export function filterImeiRows(rows, searchTerm, getCode = (row) => row?.imeiCode ?? row?.imeiNo) {
  const term = normalizeImeiCode(searchTerm).toLowerCase()
  if (!term) return [...(rows || [])]
  return (rows || []).filter((row) =>
    normalizeImeiCode(getCode(row)).toLowerCase().includes(term),
  )
}

export function buildSttRows(rows) {
  return (rows || []).map((row, index) => ({
    ...row,
    stt: index + 1,
  }))
}

export function countSelected(products) {
  return (products || []).filter((p) => p.selected).length
}

export function setStockImeiSelected(productsTerm, item, selected) {
  if (!item) return
  const value = !!selected
  const code = normalizeImeiCode(item.imeiCode || item.imeiNo)
  const master = (productsTerm || []).find(
    (row) => normalizeImeiCode(row.imeiCode || row.imeiNo) === code,
  )
  if (master) {
    master.selected = value
  }
  item.selected = value
}

export function parseManualImeisFromString(imeiString) {
  if (!imeiString || !String(imeiString).trim()) return []
  return String(imeiString)
    .split(',')
    .map((code) => normalizeImeiCode(code))
    .filter(Boolean)
    .map((imeiCode) => ({ imeiCode, selected: true }))
}

export function appendMissingSelectedImeisToList(products, product, currentImeis) {
  const list = products || []
  const seen = new Set(list.map((p) => normalizeImeiCode(p.imeiCode || p.imeiNo)))
  ;(currentImeis || []).forEach((code) => {
    const c = normalizeImeiCode(code)
    if (!c || seen.has(c)) return
    seen.add(c)
    list.push({
      id: `bill-imei-${c}`,
      imeiNo: c,
      imeiCode: c,
      productName: product?.productName,
      selected: true,
      sellingPrice: product?.sellingPriceMask || product?.sellingPrice || 0,
    })
  })
  return list
}

/**
 * Filter stock rows; mutates sourceTerm for exact-match auto-select (barcode scan).
 * @returns {{ filtered: array, hasExactMatch: boolean }}
 */
export function filterStockImeis(sourceTerm, searchTerm) {
  const normalizedSearchTerm = normalizeImeiCode(searchTerm).toLowerCase()
  let hasExactMatch = false

  const filtered = (sourceTerm || []).filter((productImei) => {
    const imeis = normalizeImeiCode(productImei.imeiCode || productImei.imeiNo).toLowerCase()
    if (normalizedSearchTerm && imeis === normalizedSearchTerm) {
      productImei.selected = true
      hasExactMatch = true
    }
    return !normalizedSearchTerm || imeis.includes(normalizedSearchTerm)
  })

  return { filtered, hasExactMatch }
}

export function dedupeManualImeis(inputImeis, newRows) {
  const existing = new Set(
    (inputImeis || []).map((row) => normalizeImeiCode(row?.imeiCode)).filter(Boolean),
  )
  const added = []
  ;(newRows || []).forEach((row) => {
    const code = normalizeImeiCode(row?.imeiCode)
    if (!code || existing.has(code)) return
    existing.add(code)
    added.push({ imeiCode: code, selected: true })
  })
  return [...(inputImeis || []), ...added]
}

/**
 * Apply danh sách IMEI từ modal "Nhập IMEI".
 * Modal đã hydrate full list → replace, không merge với IMEI cũ trên productItem.
 */
export function applyManualImeis(inputImeis) {
  const codes = (inputImeis || [])
    .map((row) => normalizeImeiCode(row?.imeiCode))
    .filter(Boolean)
  const uniqueCodes = [...new Set(codes)]
  return {
    codes: uniqueCodes,
    imeiString: uniqueCodes.join(','),
    quantity: uniqueCodes.length,
  }
}

/** Apply modal "Chọn IMEI" — replace theo các dòng đang tick selected. */
export function applyChooseImeis(products) {
  const selectedImeis = (products || []).filter((product) => product.selected)
  const codes = selectedImeis
    .map((row) => normalizeImeiCode(row?.imeiCode ?? row?.imeiNo))
    .filter(Boolean)
  const uniqueCodes = [...new Set(codes)]
  return {
    codes: uniqueCodes,
    imeiString: uniqueCodes.join(','),
    quantity: uniqueCodes.length,
  }
}

/** Logic merge cũ (union) — chỉ dùng trong test regression. */
export function applyManualImeisLegacyMerge(currentImeiString, inputImeis) {
  const { codes: selectedImeiCodes } = applyManualImeis(inputImeis)
  const currentImeis = currentImeiString
    ? currentImeiString
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean)
    : []
  const merged = [...new Set([...currentImeis, ...selectedImeiCodes])]
  return {
    codes: merged,
    imeiString: merged.join(','),
    quantity: merged.length,
  }
}
